import { useState, useCallback, useEffect } from 'react';
import { Card, Tabs, Button, InlineStack } from '@shopify/polaris';
import { CardHeading } from '../../../shared/components/cards/CardHeading.jsx';
import TiptapEditor from '../../../shared/components/TiptapEditor.jsx';

const LANGUAGE_TABS = [
  { id: 'en', content: 'English' },
  { id: 'nl', content: 'Nederlands' },
  { id: 'fr', content: 'Français' }
];

const LANG_FIELDS = {
  0: 'TechnicalDescription',
  1: 'TechnicalDescriptionNL',
  2: 'TechnicalDescriptionFR'
};

export default function TechDescCard({ productId, product, onProductChange }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localValues, setLocalValues] = useState({
    TechnicalDescription: product.TechnicalDescription || null,
    TechnicalDescriptionNL: product.TechnicalDescriptionNL || null,
    TechnicalDescriptionFR: product.TechnicalDescriptionFR || null
  });

  // Sync localValues when product prop changes (initial load, after save refetch)
  useEffect(() => {
    if (!dirty) {
      setLocalValues({
        TechnicalDescription: product.TechnicalDescription || null,
        TechnicalDescriptionNL: product.TechnicalDescriptionNL || null,
        TechnicalDescriptionFR: product.TechnicalDescriptionFR || null
      });
    }
  }, [product.TechnicalDescription, product.TechnicalDescriptionNL, product.TechnicalDescriptionFR]);

  const images = product.TechDescImages ? (typeof product.TechDescImages === 'string' ? JSON.parse(product.TechDescImages) : product.TechDescImages) : [];

  const handleContentChange = useCallback(
    (json) => {
      const field = LANG_FIELDS[selectedTab];
      setLocalValues((prev) => ({ ...prev, [field]: json }));
      setDirty(true);
    },
    [selectedTab]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          TechnicalDescription: localValues.TechnicalDescription,
          TechnicalDescriptionNL: localValues.TechnicalDescriptionNL,
          TechnicalDescriptionFR: localValues.TechnicalDescriptionFR
        })
      });
      if (res.ok) {
        setDirty(false);
        if (onProductChange) onProductChange();
      }
    } catch (err) {
      console.log('Error saving tech desc:', err);
    }
    setSaving(false);
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`/api/products/${productId}/tech-images`, {
        method: 'POST',
        body: formData
      });
      if (res.ok && onProductChange) onProductChange();
    } catch (err) {
      console.log('Error uploading image:', err);
    }
  };

  const handleImageDelete = async (key) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const res = await fetch(`/api/products/${productId}/tech-images`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key })
      });
      if (res.ok && onProductChange) onProductChange();
    } catch (err) {
      console.log('Error deleting image:', err);
    }
  };

  const getImageUrl = useCallback(
    async (key) => {
      const res = await fetch(`/api/products/${productId}/tech-images/url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key })
      });
      const data = await res.json();
      return data.url;
    },
    [productId]
  );

  const currentField = LANG_FIELDS[selectedTab];

  return (
    <Card>
      <InlineStack align="space-between" blockAlign="center">
        <CardHeading title="Technical Description" />
        {dirty && (
          <Button variant="primary" size="slim" onClick={handleSave} loading={saving}>
            Save
          </Button>
        )}
      </InlineStack>
      <div style={{ marginTop: '12px' }}>
        <Tabs tabs={LANGUAGE_TABS} selected={selectedTab} onSelect={setSelectedTab} fitted>
          <div style={{ marginTop: '12px' }}>
            <TiptapEditor
              content={localValues[currentField]}
              onChange={handleContentChange}
              images={images}
              onImageUpload={handleImageUpload}
              onImageDelete={handleImageDelete}
              getImageUrl={getImageUrl}
            />
          </div>
        </Tabs>
      </div>
    </Card>
  );
}
